import "./archive-page-facets.scss";

import { useState, useEffect, useId, useRef } from "react";
import * as Checkbox from "@radix-ui/react-checkbox";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useBreakpoint } from "design-system/hooks/use-breakpoint";
import { CtaLink } from "design-system/components/primitives/cta-link/cta-link";
import { EditAttributes } from "design-system/types/types";
import { Icon } from "design-system/components/icons/icon";

export interface ArchivePageFacetItem {
  name?: string;
  value?: string;
  checked?: boolean;
  number?: string;
  items?: Array<ArchivePageFacetItem>;
}

export interface ArchivePageFacetsProps {
  items?: Array<ArchivePageFacetItem>;
  title?: string;
  onFacetChanged?: (item: ArchivePageFacetItem, checked: boolean) => void;

  editAttributes?: {
    fieldLabel: EditAttributes;
  };
}

/**
 * ## Implementation Notes
 * - This component utilizes the [Radix Checkbox](https://www.radix-ui.com/docs/primitives/components/checkbox) and [Radix Collapsible](https://www.radix-ui.com/docs/primitives/components/collapsible) components
 *
 * ## See it in use on...
 * - An [archive page](/story/example-pages-archive-pages-story-archive--story)
 *
 * - **`id: P-001-000-00`**
 */
export function ArchivePageFacets({
  items = [],
  title,
  onFacetChanged,
  editAttributes,
}: ArchivePageFacetsProps) {
  const INITIAL_ITEMS_LENGTH = 5;

  const { isDesktop } = useBreakpoint();
  const [isOpen, setIsOpen] = useState(false);
  const [shownItemLength, setShownItemLength] = useState(INITIAL_ITEMS_LENGTH);
  const moreRef = useRef<HTMLAnchorElement & HTMLButtonElement>(null);
  const setFocusAndLength = () => {
    const parentSiblingElement = moreRef.current?.parentNode
      ?.previousSibling as HTMLElement;
    if (parentSiblingElement) {
      const childButton = parentSiblingElement.querySelector("button");
      if (childButton) childButton.focus();
    }
    setShownItemLength(items.length);
  };
  const [truncatedItems, setTruncatedItems] = useState<
    Array<ArchivePageFacetItem>
  >(items.slice(0, shownItemLength));

  useEffect(() => {
    if (isDesktop) {
      setIsOpen(true);
    }
    return () => setIsOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    setTruncatedItems(items.slice(0, shownItemLength));
  }, [items, shownItemLength]);

  if (items.length === 0) return null;

  return (
    <Collapsible.Root
      className="hbs-facets"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <fieldset>
        <Collapsible.Trigger className="hbs-facets__accordion-header-button">
          <legend className="hbs-facets__title" {...editAttributes?.fieldLabel}>
            {title}
          </legend>
          <Icon name="caretDown" className="hbs-facets__caret" />
        </Collapsible.Trigger>

        <Collapsible.CollapsibleContent className="hbs-facets__accordion-menu">
          <FacetMenu
            items={truncatedItems}
            onFacetChanged={onFacetChanged}
            depth={1}
          />
          <div className="hbs-facets__item">
            {truncatedItems.length < items.length && (
              <CtaLink
                className="hbs-facets__more-button"
                isSmall
                hideIcon
                onClick={() => setFocusAndLength()}
                ref={moreRef}
                aria-label={`More ${title}`}
              >
                More
              </CtaLink>
            )}
          </div>
        </Collapsible.CollapsibleContent>
      </fieldset>
    </Collapsible.Root>
  );
}

type FacetMenuProps = {
  items: Array<ArchivePageFacetItem>;
  depth: number;
  onFacetChanged?: (item: ArchivePageFacetItem, checked: boolean) => void;
};

function FacetMenu({ items, depth, onFacetChanged }: FacetMenuProps) {
  const facetsID = useId();

  const onCheckedChange = (
    checked: boolean | string,
    item: ArchivePageFacetItem,
  ) => {
    if (onFacetChanged) {
      if (typeof checked === "boolean") {
        onFacetChanged(item, checked);
      } else if (typeof checked === "string" && checked === "indeterminate") {
        onFacetChanged(item, false);
      }
    }
  };

  return (
    <>
      {items.map((item, i) => (
        <>
          <div className="hbs-facets__item" key={item.value}>
            <Checkbox.Root
              className="hbs-facets__checkbox"
              id={`facet-${i + 1}-${facetsID}`}
              checked={item.checked}
              onCheckedChange={(checked) => onCheckedChange(checked, item)}
            >
              <Checkbox.Indicator>
                <Icon name="check" className="hbs-facets__check-icon" />
              </Checkbox.Indicator>
              <span className="hbs-global-visually-hidden">
                Filter by {item.name} {item.number}
              </span>
            </Checkbox.Root>
            <label
              aria-hidden="true"
              className="hbs-facets__label"
              htmlFor={`facet-${i + 1}-${facetsID}`}
            >
              {item.name}{" "}
              {item.number && (
                <span className="hbs-facets__number">{item.number}</span>
              )}
            </label>
            {item.items && item.items.length > 0 && depth < 3 && (
              <div className="hbs-facets__sub-menu">
                <FacetMenu
                  items={item.items}
                  onFacetChanged={onFacetChanged}
                  depth={depth + 1}
                />
              </div>
            )}
          </div>
        </>
      ))}
    </>
  );
}
